import {ComponentProps, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {BatchInvitationsRow} from '../BatchInvitationsRow/BatchInvitationsRow';
import {BatchInvitationProgressItem} from '../types/screeningExternalApplicantsTypes';
import {getBatchInvitationStatusTranslationKey} from '../utils/batchInvitationsProgressUtils';

import {BasicTable} from '../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../components/BasicTable/createBasicTableColumn';

interface Props {
  data: BatchInvitationProgressItem[];
  isLoading: boolean;
  highlightedBatchId?: string | null;
  onDetails: (batchId: string) => void;
}

type RowProps = ComponentProps<typeof BatchInvitationsRow>;

export const BatchInvitationsTable = ({data, isLoading, highlightedBatchId, onDetails}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'screeningModule.batchInvitationsProgress'});

  const columns = useMemo(
    () => [
      createBasicTableColumn('created-at', t('columns.createdAt'), {minWidth: '180px'}),
      createBasicTableColumn('status', t('columns.status'), {minWidth: '180px'}),
      createBasicTableColumn('progress', t('columns.progress'), {minWidth: '160px'}),
      createBasicTableColumn('details', t('columns.details'), {
        minWidth: '120px',
        sx: {
          textAlign: 'right',
          '& .MuiTypography-root': {
            width: '100%',
            justifyContent: 'flex-end',
          },
        },
      }),
    ],
    [t]
  );

  const rowData = useMemo<RowProps[]>(
    () =>
      data.map((item) => ({
        id: item.batchId,
        batchId: item.batchId,
        createdAt: item.createdAt,
        statusLabel: t(`statuses.${getBatchInvitationStatusTranslationKey(item.status)}`),
        progressLabel: `${item.processedRowsCount}/${item.totalRowsCount}`,
        detailsLabel: t('detailsAction'),
        onDetails,
        isHighlighted: highlightedBatchId === item.batchId,
      })),
    [data, onDetails, highlightedBatchId, t]
  );

  return <BasicTable columns={columns} Row={BatchInvitationsRow} rowData={rowData} isLoading={isLoading} />;
};
